import { ApiError, commonsApiClient } from "./client";
import {
  AuthenticationResponse,
  RegistrationPayload,
  User,
} from "../types/api";

/**
 * Logs in a user returning the user object
 * and the JWT Token both as a HttpOnly cookie
 * and as a string as part of the response.
 *
 * @param email - The email of the user
 * @param password - The password of the user
 * @returns The response from the login API
 */
export const login = async (
  email: string,
  password: string
): Promise<AuthenticationResponse> => {
  try {
    const response = await commonsApiClient.post<AuthenticationResponse>(
      `/api/login`,
      { email, password }
    );
    return response;
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

/**
 * Registers a new user with the provided details.
 *
 * @param data - The user's registration payload.
 * @returns The response from the registration API.
 */
export const register = async (
  data: RegistrationPayload
): Promise<AuthenticationResponse> => {
  try {
    const response = await commonsApiClient.post<AuthenticationResponse>(
      `/api/register`,
      data
    );
    return response;
  } catch (error) {
    console.error("Registration error:", error);
    throw error;
  }
};

/**
 * Sends a password reset link to the user's email
 * @param email - The email of the user
 * @returns The response from the forgot password API
 */
export const forgotPassword = async (
  email: string
): Promise<{ status: string }> => {
  try {
    const response = await commonsApiClient.post<{ status: string }>(
      `/api/forgot-password`,
      { email }
    );
    return response;
  } catch (error) {
    console.error("Forgot password error:", error);
    throw error;
  }
};

/**
 * Resets the user's password
 * @param token - The token used to reset the password
 * @param email - The email of the user
 * @param password - The new password
 * @returns The response from the reset password API
 */
export const resetPassword = async (
  token: string,
  email: string,
  password: string,
  passwordConfirmation: string
): Promise<{ status: string }> => {
  try {
    const response = await commonsApiClient.post<{ status: string }>(
      `/api/reset-password`,
      { token, email, password, password_confirmation: passwordConfirmation }
    );
    return response;
  } catch (error) {
    console.error("Reset password error:", error);
    throw error;
  }
};

/**
 * Fetches the currently authenticated user from the API.
 * This function is used to check the validity of the HttpOnly cookie
 * and retrieve user data without exposing the JWT token to the client-side.
 *
 * @returns A Promise that resolves to the User object if authenticated, or null otherwise.
 */
export const fetchAuthenticatedUser = async (): Promise<User | null> => {
  try {
    const response = await commonsApiClient.get<User>(`/api/user`, {
      withCredentials: true,
    });
    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      // If the user is not authenticated, return null without erroring
      if (error.status === 401) {
        return null;
      }
    }
    console.error("Authentication check failed:", typeof error, error);
    return null;
  }
};

/**
 * Logs out the user by sending a request to the backend to clear the HttpOnly cookie.
 *
 * @returns A Promise that resolves when the logout request is complete.
 */
export const logout = async (): Promise<void> => {
  try {
    await commonsApiClient.post(`/api/logout`, {});
  } catch (error) {
    console.error("Logout failed:", error);
    throw error;
  }
};

/**
 * Sends a verification email to the user.
 *
 * @returns A Promise that resolves when the verification email is sent.
 */
export const sendVerificationEmail = async (): Promise<{ status: string }> => {
  try {
    const response = await commonsApiClient.post<{ status: string }>(
      `/api/email/verification-notification`,
      {}
    );
    return response;
  } catch (error) {
    console.error("Send verification email error:", error);
    throw error;
  }
};

/**
 * Verifies the user's email with the provided token and ID.
 *
 * @param id - The user's ID.
 * @param hash - The hash from the verification link.
 * @param expires - The expiration timestamp from the verification link.
 * @returns A Promise that resolves when the email is verified.
 */
export const verifyEmail = async (
  id: string,
  hash: string,
  expires: string
): Promise<{ status: string }> => {
  try {
    const response = await commonsApiClient.post<{ status: string }>(
      `/api/email/verify/${id}`,
      { hash, expires }
    );
    return response;
  } catch (error) {
    console.error("Verify email error:", error);
    throw error;
  }
};
